<template>
  <div>
    <Modal
      role="dialog"
      aria-labelledby="switch-account-header"
      aria-describedby="switch-account-instructions"
      justifyContent="flex-start"
      textAlign="flex-start"
      class="w-[90vw] h-[80vh] max-w-[580px] max-h-[520px]"
      :show="authStore.showAuthSwitchDialog"
      :bodyFullHeight="true"
      @close="closeModal"
    >
      <template #header>
        <div
          class="font-bold text-primary"
          id="switch-account-header"
          text-primary
        >
          Switch Account
        </div>
      </template>

      <div :style="`width: 700px; height: 100%`">
        <div class="flex flex-row items-center">
          <p class="text-[26px]">Confirm Account</p>
        </div>
        <div
          class="flex flex-row items-center"
          id="switch-account-instructions"
        >
          <p>
            Please enter your password to switch to your
            {{ authStore.type == 'donorAccount' ? 'Company' : 'Donor' }} Account
          </p>
        </div>
        <div class="flex flex-row items-center pt-4">
          <Form
            v-if="initialValues && authStore.showAuthSwitchDialog"
            v-slot="$form"
            :resolver="resolver"
            :initialValues="initialValues"
            :validateOnValueUpdate="true"
            ref="form"
            class="flex justify-center flex-col gap-4 w-full"
            @keydown.prevent.enter="submit"
            v-focustrap
          >
            <div class="flex flex-col gap-1">
              <span>Email</span>
              <InputText
                class="w-full"
                id="email"
                name="email"
                type="text"
                placeholder="Email"
                disabled
                data-test="email-field"
              />
              <Message
                v-if="$form.email?.invalid"
                severity="error"
                size="small"
                variant="simple"
                >{{ $form.email.error?.message }}</Message
              >
            </div>
            <div class="flex flex-col gap-1">
              <label for="password">Password</label>
              <InputText
                class="w-full"
                id="password"
                name="password"
                type="password"
                ref="passwordField"
                data-test="password-field"
                aria-required="true"
                aria-describedby="password-error"
                autofocus
              />
              <Message
                v-if="$form.password?.invalid"
                id="password-error"
                severity="error"
                size="small"
                variant="simple"
                data-test="error-message"
                >{{ $form.password.error?.message }}</Message
              >
            </div>
          </Form>
        </div>
      </div>

      <template #footer>
        <Button
          variant="link"
          :class="{ invisible: !props.hasBack }"
          @click="closeModal"
        >
          Back
        </Button>
        <Button
          class="w-[120px]"
          :loading="loading"
          @click="submit"
          data-testid="submit-button"
          data-test="submit-button"
        >
          Log In
        </Button>
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { ref, defineEmits, defineProps, computed, watch } from 'vue';
import { useAuthStore } from '@/stores/AuthStore.js';
import { zodResolver } from '@primevue/forms/resolvers/zod';
import { z } from 'zod';
import Modal from '@/components/Modal.vue';
import { useDonorStore } from '@/stores/DonorStore.js';
import { useRouter } from 'vue-router';
import { useToastStore } from '@/stores/ToastStore.js';

const router = useRouter();
const form = ref(null);
const passwordField = ref(null);

const authStore = useAuthStore();
const donorStore = useDonorStore();
const loading = ref(false);

const emit = defineEmits(['closed']);

const props = defineProps({
  hasBack: {
    type: Boolean,
    default: true,
  },
  redirect: {
    type: Boolean,
    default: true,
  },
});

const initialValues = computed(() => {
  return {
    email: donorStore.donorInfo.email,
    password: '',
  };
});

const closeModal = () => {
  authStore.showAuthSwitchDialog = false;
  emit('closed');
};

const resolver = ref(
  zodResolver(
    z.object({
      password: z.string().min(1, { message: 'Password is required.' }),
      email: z.string().min(1, { message: 'Email is required.' }).email({ message: 'Invalid email address.' }),
    }),
  ),
);

const submit = async () => {
  if (form.value) {
    await form.value.validate();
  }
  if (form.value.valid) {
    loading.value = true;
    try {
      await authStore.switchAccount(form.value.states.email.value, form.value.states.password.value);
      closeModal();
      if (props.redirect) {
        authStore.showAuthSwitchDialog = false;
        router.push(authStore.type == 'companyAccount' ? '/company/overview' : '/');
      }
    } catch (e) {
      useToastStore().add({
        severity: 'error',
        summary: 'Error',
        detail: e.message,
        life: 3000,
      });
    } finally {
      loading.value = false;
    }
  }
};
</script>
